import type { ReactNode } from 'react'
import React from 'react'
import { css, cx } from '@linaria/core'
import { boolAttr, sanitizeProps } from '@fs/zion-frontend-friends'

type Size = 'xs' | 'sm' | 'md' | 'lg'
type Direction = 'up' | 'down' | 'forward' | 'backward'
type Reflect = 'vertical' | 'horizontal'

const sizes = {
  xs: 18,
  sm: 24,
  md: 36,
  lg: 48,
}

export type IconProps = {
  /**
   * <details>
   *   <summary>**See available sizes.**</summary>
   * - **xs**: 18x18
   * - **sm**: 24x24
   * - **md**: 40x40
   * - **lg**: 48x48
   * </details>
   */
  size?: Size

  /**
   * The direction the icon is pointing.
   *
   * **NOTE:** This prop is ignored by common icons, but allowed for interoperability between icons
   */
  direction?: Direction

  /**
   * Whether the icon is in its toggled-state.
   *
   * **NOTE:** This prop is ignored by common icons, but allowed for interoperability between icons
   */
  toggle?: boolean

  /**
   * Reflection of icon.
   *
   * **NOTE:** This prop is ignored by common icons, but allowed for interoperability between icons
   */
  reflect?: Reflect

  /** Fill color of icon */
  color?: string

  /**
   * Background color.
   *
   * Gives the Icon a circle background in the color provided.
   * If no background is provided, it defaults to transparent with no circle.
   */
  background?: string

  /** What screen-readers should call this icon */
  'aria-label'?: string

  /**
   * What aria-role you'd like to give this icon
   *
   * Normally, you are required to use aria-label to describe what this icon means. However, if
   * the icon has no visual meaning, use `role="presentation"` instead of an aria-label.
   */
  role?: string

  /**
   * Whether you need your icon to align horizontally with keylines.
   * This cuts a little space off the sides, so that the icon itself will hug the edges of your layout.
   */
  dense?: boolean

  /**
   * If your Icon is being used inside of a text node, the Icon will sometimes not align well with
   * the text. In most cases, you do not need this prop, because the Typographic elements in ZIon UI
   * are already tuned to accept Icons and have them align well. But in some cases, you may need it.
   * For example, if you put an Icon inside a table cell with some other text, you will need to set
   * this property.
   */
  alignWithText?: boolean
}

export type IconComponent = (props: IconProps) => JSX.Element

type BaseIconProps = IconProps & {
  className?: string
  children?: ReactNode
}

const spinningCss = css`
  animation: rotate-spinner 1s linear infinite;
  @keyframes rotate-spinner {
    to {
      transform: rotate(360deg);
    }
  }
  // Users need to be able to tell when something is loading
  // This rule allows icons to spin even if OS settings for reduced motion are set so users can tell content is being
  // loaded/uploaded.
  @media (prefers-reduced-motion: reduce) {
    animation-iteration-count: infinite !important;
    animation-duration: 1s !important;
  }
`

const directionCss = css`
  transition: transform 0.3s;
  &[icon-direction='forward'] {
    transform: rotate(0deg);
  }
  &[icon-direction='up'] {
    transform: rotate(-90deg);
  }
  &[icon-direction='backward'] {
    transform: rotate(180deg);
  }
  &[icon-direction='down'] {
    transform: rotate(90deg);
  }
  [dir='rtl'] &[icon-direction='up'] {
    transform: rotate(90deg);
  }
  [dir='rtl'] &[icon-direction='down'] {
    transform: rotate(-90deg);
  }
`

const reflectCss = css`
  transition: transform 0.3s;
  &[icon-reflect='horizontal'] {
    transform: scaleX(-1);
  }
  &[icon-reflect='vertical'] {
    transform: scaleY(-1);
  }
`

const shapeCss = css`
  display: inline-block;
  overflow: hidden;
  flex-shrink: 0;
  vertical-align: middle;
  &[icon-size='xs'] {
    --icon-circle-size: 28px;
  }
  &[icon-size='sm'] {
    --icon-circle-size: 40px;
  }
  &[icon-size='md'] {
    --icon-circle-size: 64px;
  }
  &[icon-size='lg'] {
    --icon-circle-size: 88px;
  }

  /* This is to fix https://fhjira.churchofjesuschrist.org/browse/ZIONUI-558
  * without causing weird things to happen to other components.
  */
  margin-top: calc(var(--icon-inline-text, 0) * var(--icon-circle-size));

  &[data-dense] {
    margin-inline-start: calc(var(--icon-circle-size) * -0.1);
    margin-inline-end: calc(var(--icon-circle-size) * -0.1);
  }

  &[with-background] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 500px;
    width: var(--icon-circle-size);
    height: var(--icon-circle-size);
  }

  &[align-with-text] {
    --icon-inline-text: -0.2;
  }
`

const svgCss = css`
  display: block;
`

/** This is just for Storybook */
export const GenericIcon = (props: IconProps): JSX.Element => <div {...props} />

/**
 * This icon library is based on the [Material Design specification of outlined icons](https://material.io/design/iconography/system-icons.html).
 * The list of sanctioned Zion Icons are selected from icon sets provided by [Material-io](https://material.io/resources/icons/?style=outline),
 * licensed [Nova Icons](https://www.streamlineicons.com/nova/), and in-house custom-drawn icons.
 *
 * ## Icon Meanings
 * Each icon has an intended meaning. We want to make sure that the intended meaning of each icon stays
 * consistent across the site. If you would like to use a Zion Icon in a way that veers from its intented
 * meaning, or if you have a need for a new icon, it should be addressed with UX.
 */
export const IconComponentForStorybook = ({
  Icon,
  ...props
}: IconProps & { Icon: typeof GenericIcon }): JSX.Element => {
  return <Icon {...props} />
}

export const BaseIcon = ({
  role,
  'aria-label': label,
  size = 'sm',
  color = 'currentColor',
  className,
  children,
  background = 'transparent',
  dense = false,
  alignWithText = false,

  /** [IGNORED]: For interoperability between icons */
  direction, // eslint-disable-line -- ignored
  reflect, // eslint-disable-line -- ignored
  toggle, // eslint-disable-line -- ignored

  ...props
}: BaseIconProps): JSX.Element => (
  <span
    className={shapeCss}
    style={{ background }}
    icon-size={size}
    with-background={boolAttr(background !== 'transparent')}
    align-with-text={boolAttr(alignWithText)}
    data-dense={boolAttr(dense)}
  >
    <svg
      {...sanitizeProps(props)}
      className={cx(className, svgCss)}
      style={{ color }}
      fill="currentColor"
      viewBox="0 0 24 24"
      width={sizes[size]}
      height={sizes[size]}
      // Here we dynamically assign the role based on if there is a label provided.
      // This makes "presentation" the default role, since most of the time
      // an icon really only provides visual accompaniment to some text that is
      // colocated with the Icon. Lone Icons are rare, so we allow the consumer
      // to specify an label to cover that case. IconButton already requires a label.
      role={role || (label ? 'img' : 'presentation')}
      aria-label={label}
    >
      {children}
    </svg>
  </span>
)

export const SpinningIcon = ({ children, ...props }: BaseIconProps): JSX.Element => (
  <BaseIcon {...props} className={spinningCss}>
    {children}
  </BaseIcon>
)

export const DirectionalIcon = ({ direction = 'forward', children, ...props }: BaseIconProps): JSX.Element => (
  <BaseIcon {...props} className={directionCss} icon-direction={direction}>
    {children}
  </BaseIcon>
)

export const ReflectableIcon = ({ reflect, children, ...props }: BaseIconProps): JSX.Element => (
  <BaseIcon {...props} className={reflectCss} icon-reflect={reflect}>
    {children}
  </BaseIcon>
)

export const ToggleIcon = ({ toggle = false, children, ...props }: BaseIconProps): JSX.Element => (
  <BaseIcon {...props} icon-toggle={boolAttr(toggle)}>
    {children}
  </BaseIcon>
)

export const ToggleDirectionalIcon = ({
  direction = 'forward',
  toggle = false,
  children,
  ...props
}: BaseIconProps): JSX.Element => (
  <BaseIcon {...props} className={directionCss} icon-toggle={boolAttr(toggle)} icon-direction={direction}>
    {children}
  </BaseIcon>
)
